import { useMoviesStore, useRestrictedBySpecialCard } from "@multicines/stores";
import { useShoppingCartStore } from "@multicines/stores";
import { getShoppingCartProductsByVendor } from "@multicines/utils";
import { getIsLoyaltyTicket, titleCase } from "@multicines/utils";
import { TICKETS_VENDOR } from "@multicines/utils";
import { getProductTotals } from "artisn/products";
import { getShoppingCartProducts, subtractProduct } from "artisn/shopping-cart";
import React, { useEffect, useMemo, useState } from "react";

import Styles from "./ShoppingCartTicket.styles";
import { ShoppingCartTicketProps as Props } from "./ShoppingCartTicket.types";
import useShoppingCartTicketAnalytics from "./useShoppingCartTicketAnalytics";
import Counter from "../Counter/Counter";
import InfoMessage from "../InfoMessage/InfoMessage";
import { notify } from "config/bugsnag.config";
import CONSTANTS from "config/constants";
import useI18n from "hooks/useI18n";
import useShoppingCart from "hooks/useShoppingCart/useShoppingCart";
import useUtils from "hooks/useUtils";
import { useAuthStore } from "stores/auth/auth.store";
import { useStoresStore } from "stores/stores/stores.store";
import { createErrorNotification } from "utils/notifications.utils";
import { getIsSpecialPerson } from "utils/specialPerson.utils";
const { DEFAULT_TICKET_TYPE, ARTISN } = CONSTANTS;
const { CHANNEL_ID } = CONSTANTS.ARTISN;
const { ACCOUNT_ID } = ARTISN;

const ShoppingCartTicket: React.FC<Props> = props => {
  const { className, product, onChange, disabledAdd, ticketCategory } = props;
  const { remainingPoints, onIsAdding, qtyAvailable = 0 } = props;
  const isAnonymous = useAuthStore(state => state.isAnonymous);
  const uid = useAuthStore(state => state.uid);
  const { formatByCurrency } = useUtils();
  const selectedStore = useStoresStore(store => store.selectedStore);
  const shoppingCart = useShoppingCartStore(store => store.shoppingCart);
  const mustUsePoints = useMoviesStore(store => store.mustUsePoints);
  const setMustUsePoints = useMoviesStore(store => store.setMustUsePoints);
  const specialTicketType = useMoviesStore(store => store.specialTicketType);
  const setSpecialTicketType = useMoviesStore(
    store => store.setSpecialTicketType
  );
  const { addProduct, createShoppingCart } = useShoppingCart();
  const { logSelectTickets, logRemoveTickets } =
    useShoppingCartTicketAnalytics();
  const [disabledByTicketType, setDisabledByTicketType] = useState(false);

  const { netPrice } = getProductTotals(product, 1) ?? {};

  const { name, productId, maxAmountForSale = 0 } = product;

  const [ticketsStore] = selectedStore ?? [];
  const { vendor } = ticketsStore ?? {};
  const { id: selectedTicketVendorId } = vendor ?? {};
  const cartProducts = useMemo(
    () =>
      getShoppingCartProductsByVendor(
        shoppingCart,
        selectedTicketVendorId ?? TICKETS_VENDOR.id
      ),
    [selectedTicketVendorId, shoppingCart]
  );

  const cartProduct = useMemo(
    () =>
      cartProducts?.find(item => {
        return item.productId === productId;
      }),
    [cartProducts, productId]
  );
  const { additionalInfo = {} } = product;
  const { priceInPoints } = additionalInfo;
  const { specialTicketType: localSpecialTicketType = DEFAULT_TICKET_TYPE } =
    additionalInfo;
  const { specialPersonType } = additionalInfo;
  const t = useI18n();

  const { specialTicketType: specialCard } = additionalInfo;
  const { isRestricted = true } = useRestrictedBySpecialCard(specialCard);
  const isSpecialPerson = getIsSpecialPerson(specialPersonType);

  const addHandler = async () => {
    onIsAdding(true);

    if (!uid || typeof isAnonymous === "undefined") {
      createErrorNotification(t.errors.invalidUser, undefined);
      onIsAdding(false);
      return;
    }

    if (!ticketsStore) {
      createErrorNotification(t.errors.storeNotFound, undefined);
      onIsAdding(false);
      return;
    }

    try {
      if (!shoppingCart) {
        const { id } =
          (await createShoppingCart?.({ channelId: CHANNEL_ID })) ?? {};
        if (!id) {
          throw new Error();
        }
        //INFO: Wait for the shopping cart to be created the first time
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    } catch (error) {
      notify(error, "addHandler - ShoppingCartTicket");
      createErrorNotification(t.errors.shoppingCart.createCart, undefined);
      onIsAdding(false);
      return;
    }

    // @ts-ignore add ticketCategory as additional info
    product.attributes.ticketCategory = ticketCategory;
    if (!specialTicketType) {
      setSpecialTicketType(localSpecialTicketType);
    }
    if (priceInPoints) {
      setMustUsePoints(true);
    }
    const { id } =
      (await addProduct?.(product, {
        amount: 1,
        store: ticketsStore,
        priceCategory: priceInPoints ? "POINTS" : "NORMAL"
      })) ?? {};

    if (!id) {
      createErrorNotification(t.errors.shoppingCart.addToCart, undefined);
      onIsAdding(false);
      return;
    }

    logSelectTickets({ quantity: 1 });
    onIsAdding(false);
  };

  const subtractHandler = async () => {
    if (!uid || typeof isAnonymous === "undefined") {
      createErrorNotification(t.errors.invalidUser, undefined);
      return;
    }

    if (!ticketsStore) {
      createErrorNotification(t.errors.storeNotFound, undefined);
      return;
    }

    logRemoveTickets({ quantity: 1 });
    const newCart = await subtractProduct(product, {
      amount: 1,
      store: ticketsStore,
      anonymous: isAnonymous,
      priceCategory: priceInPoints ? "POINTS" : "NORMAL",
      accountId: ACCOUNT_ID,
      customerId: uid
    });
    if (!newCart) return;

    const products = getShoppingCartProducts(newCart);
    const productsInCart = products.reduce((acc, next) => {
      return acc + next.amount;
    }, 0);
    if (!productsInCart) {
      setSpecialTicketType(undefined);
      setMustUsePoints(false);
    }
  };

  useEffect(() => {
    // INFO: If a special ticket was added, no other ticket can be added unless it is the same special ticket.
    const disabledProvider =
      !!specialTicketType && localSpecialTicketType !== specialTicketType;
    // INFO: If a points ticket was added, no other ticket can be added unless it is also a points ticket.
    const onlyPoints = mustUsePoints && typeof priceInPoints === "undefined";
    // INFO: If a normal or $0 value ticket was added, points tickets cannot be added.
    const onlyCash =
      !!cartProducts?.length &&
      !mustUsePoints &&
      typeof priceInPoints !== "undefined";
    // INFO: If the ticket is a points ticket and there are not enough points, it cannot be added.
    const notEnoughPoints = priceInPoints && priceInPoints > remainingPoints;
    setDisabledByTicketType(
      disabledProvider || onlyPoints || onlyCash || notEnoughPoints
    );
  }, [
    cartProducts,
    localSpecialTicketType,
    mustUsePoints,
    priceInPoints,
    remainingPoints,
    specialTicketType
  ]);

  if (maxAmountForSale === 0) return null;

  const isLoyaltyTicket = getIsLoyaltyTicket(product);

  const isDisableAdd = disabledAdd || disabledByTicketType;
  const maxLoyaltyAdd =
    maxAmountForSale > qtyAvailable ? qtyAvailable : maxAmountForSale;
  const maxCount = isLoyaltyTicket ? maxLoyaltyAdd : maxAmountForSale;
  const maxAddCount = isDisableAdd ? 0 : maxCount;

  return (
    <Styles className={`ShoppingCartTicket ${className}`}>
      <div className="ShoppingCartTicket__dot-container ShoppingCartTicket__left-dot-container">
        <div className="ShoppingCartTicket__dot" />
      </div>
      <div className="ShoppingCartTicket__content">
        <div className="ShoppingCartTicket__content__left">
          <p className="ShoppingCartTicket__title">{titleCase(name)}</p>
          <p className="ShoppingCartTicket__price">
            {priceInPoints ? `${priceInPoints}pts` : formatByCurrency(netPrice)}
          </p>
          {isLoyaltyTicket ? (
            <p className="ShoppingCartTicket__price">{`Cantidad disponible: ${qtyAvailable}`}</p>
          ) : null}
          {/* TODO: Mock only testing */}
          {/* <p className="ShoppingCartTicket__price">{`Total de tickets: ${maxAmountForSale}`}</p> */}
        </div>
        <div className="ShoppingCartTicket__content__right">
          {priceInPoints && priceInPoints > remainingPoints ? (
            <p className="ShoppingCartTicket__warning">
              {cartProduct?.amount
                ? t.tickets.cantAddMore
                : t.tickets.notEnoughPoints}
            </p>
          ) : null}
          <Counter
            initialValue={cartProduct?.amount}
            max={maxAddCount}
            min={0}
            onChange={onChange}
            onAdd={addHandler}
            onSubtract={subtractHandler}
            disabled={isRestricted}
          />
        </div>
      </div>
      {isSpecialPerson ? <InfoMessage message={t.tickets.info} /> : null}
      <div className="ShoppingCartTicket__dot-container ShoppingCartTicket__right-dot-container">
        <div className="ShoppingCartTicket__dot" />
      </div>
    </Styles>
  );
};

export default ShoppingCartTicket;
